<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import { resolveAssetUrl } from '../productPresentation'

const props = defineProps<{
  label: string
  modelValue?: string
  accept?: string
  buttonLabel?: string
  helperText?: string
  statusText?: string
  errorText?: string
  disabled?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
  selected: [file: File]
}>()

const picker = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const objectPreviewUrl = ref('')

function revokePreview(): void {
  if (!objectPreviewUrl.value) return
  URL.revokeObjectURL(objectPreviewUrl.value)
  objectPreviewUrl.value = ''
}

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/')
}

function isImageValue(value?: string): boolean {
  if (!value) return false
  return /^(blob:|data:image\/|https?:.*\.(png|jpe?g|webp|gif|svg))/i.test(value)
    || /\.(png|jpe?g|webp|gif|svg)$/i.test(value)
}

const previewSrc = computed(() =>
  objectPreviewUrl.value
  || (isImageValue(props.modelValue) ? resolveAssetUrl(props.modelValue || null) || '' : ''),
)
const helperLabel = computed(() =>
  props.buttonLabel || 'Choose file',
)

function applyFile(file: File): void {
  emit('selected', file)
  if (!props.modelValue) emit('update:modelValue', file.name)
  revokePreview()
  if (isImageFile(file)) {
    objectPreviewUrl.value = URL.createObjectURL(file)
  }
}

function onSelect(event: Event): void {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  applyFile(file)
  input.value = ''
}

function openPicker(): void {
  if (props.disabled) return
  picker.value?.click()
}

function onDrop(event: DragEvent): void {
  if (props.disabled) return
  isDragging.value = false
  const file = event.dataTransfer?.files?.[0]
  if (!file) return
  applyFile(file)
}

onBeforeUnmount(revokePreview)
</script>

<template>
  <label class="upload-field">
    <span>{{ label }}</span>
    <input
      class="upload-field__input"
      :value="modelValue"
      type="text"
      :disabled="disabled"
      @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
    >
    <div
      class="upload-field__dropzone"
      :class="{
        'upload-field__dropzone--dragging': isDragging,
        'upload-field__dropzone--preview': Boolean(previewSrc),
        'upload-field__dropzone--disabled': disabled,
        'upload-field__dropzone--error': errorText,
      }"
      role="button"
      :tabindex="disabled ? -1 : 0"
      :aria-disabled="disabled ? 'true' : 'false'"
      @click="openPicker"
      @keydown.enter.prevent="openPicker"
      @keydown.space.prevent="openPicker"
      @dragenter.prevent="!disabled && (isDragging = true)"
      @dragover.prevent="!disabled && (isDragging = true)"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
    >
      <input
        ref="picker"
        class="upload-field__picker"
        type="file"
        :accept="accept"
        @change="onSelect"
      >
      <img v-if="previewSrc" class="upload-field__preview" :src="previewSrc" :alt="label">
      <div v-else class="upload-field__placeholder">
        <strong>{{ helperLabel }}</strong>
        <span>Drag and drop or click to upload</span>
        <small>{{ helperText || modelValue || 'Image or file URL can be edited above.' }}</small>
      </div>
      <span v-if="previewSrc" class="upload-field__overlay">{{ helperLabel }}</span>
    </div>
    <small v-if="statusText" class="upload-field__status">{{ statusText }}</small>
    <small v-if="errorText" class="upload-field__error">{{ errorText }}</small>
  </label>
</template>

<style scoped>
.upload-field { display: grid; gap: 8px; }
.upload-field__input { width: 100%; box-sizing: border-box; padding: 9px 10px; border: 1px solid #b9c5c1; font: inherit; }
.upload-field__dropzone { position: relative; display: grid; min-height: 180px; padding: 14px; border: 1px dashed #9db3cc; border-radius: 14px; background: linear-gradient(145deg, #f8fbff 0%, #eef5fb 100%); cursor: pointer; place-items: center; transition: border-color .16s ease, background .16s ease, transform .16s ease; }
.upload-field__dropzone:hover { border-color: #3568a8; background: linear-gradient(145deg, #f5f9ff 0%, #eaf2fa 100%); }
.upload-field__dropzone--dragging { border-color: #07996f; background: linear-gradient(145deg, #edf9f4 0%, #e3f5ee 100%); transform: scale(1.01); }
.upload-field__dropzone--preview { padding: 0; overflow: hidden; }
.upload-field__dropzone--disabled { cursor: not-allowed; opacity: .72; }
.upload-field__dropzone--disabled:hover { border-color: #9db3cc; background: linear-gradient(145deg, #f8fbff 0%, #eef5fb 100%); }
.upload-field__dropzone--error { border-color: #c56f63; background: linear-gradient(145deg, #fff8f7 0%, #fdf0ee 100%); }
.upload-field__picker { display: none; }
.upload-field__placeholder { display: grid; gap: 6px; color: #53625d; text-align: center; }
.upload-field__placeholder strong { color: #173454; font-size: 14px; }
.upload-field__placeholder span { font-size: 13px; font-weight: 700; }
.upload-field__placeholder small { color: #66726e; font-size: 12px; }
.upload-field__preview { display: block; width: 100%; height: 100%; max-height: 260px; object-fit: contain; background: #fff; }
.upload-field__overlay { position: absolute; right: 12px; bottom: 12px; padding: 7px 10px; border: 1px solid rgb(255 255 255 / 75%); border-radius: 999px; color: #173454; background: rgb(255 255 255 / 88%); font-size: 12px; font-weight: 800; backdrop-filter: blur(6px); }
.upload-field__status { color: #2e5f97; }
.upload-field__error { color: #852d22; }
</style>
